import { FC } from "react"
import Image from "next/image"
import dynamic from "next/dynamic"
import classNames from "classnames"
import { readImage } from "../../app/utils/read-image"
import { BlokImage } from "../../storyblok"
import { safelyRenderHtml } from "@/util/safelyRenderHtml"
import { useGeoDataEffect } from "../../hooks/useGeoData"
import { PREMIUM_HERO_FORM_ATF_LAYOUT, PremiumHeroForm } from "./hero-form"
import type { ThumbnailItem } from "@/storyblok/sections/ThumbnailSet"

// Dynamic so this eager hero variant doesn't pull swiper into the page chunk
const ThumbnailSet = dynamic(() => import("@/storyblok/sections/ThumbnailSet"))

export interface IPetsHeroATF {
  blok: {
    title: string
    description: string
    buttonLink?: string
    buttonLabel?: string
    image?: BlokImage
    geoPostDescription?: string
    geoTitle?: string
    hasGeoCustomization?: boolean
    hasSwapCustomization?: boolean
    hasListingForm?: boolean
    animalType?: string
    bgImagePlacement?: string
    testimonial: ThumbnailItem[]
    TestimonialTitle?: string
    TestimonialSubtitle?: string
  }
}
const PetsHeroATFTestimonial: FC<IPetsHeroATF> = (props) => {
  const {
    title,
    description,
    buttonLabel,
    buttonLink,
    image,
    hasGeoCustomization = false,
    hasSwapCustomization = false,
    geoPostDescription = "Hire top-rated transporters in {location}.",
    geoTitle = "",
    hasListingForm = false,
    testimonial,
    TestimonialSubtitle,
    TestimonialTitle,
  } = props.blok
  const { geoDescription, geoSessionValue } = useGeoDataEffect(
    geoPostDescription,
  )
  const geoLocationTitle = useGeoDataEffect(geoTitle)

  return (
    <>
      <section
        id="hero-section"
        className={classNames(
          "relative md:min-h-[400px] lg:min-h-[520px] bg-secondary-100  lg:flex lg:items-start md:py-10",
        )}
      >
        <div className="flex flex-col md:flex-row w-full justify-center items-start mx-auto py-5 md:py-0 md:pb-2">
          <div className="w-full md:w-1/2 lg:w-2/3 lg:max-w-[651px]">
            <div className="max-w-4xl lg:max-w-5xl px-4">
              <div className="text-black font-inter max-w-3xl text-left md:mt-10 mb-0">
                <h1 className="text-[32px] leading-[46px] mb-2 md:leading-[70px] sm:text-[42px] md:text-[56px] font-bold lg:text-[56px] mt-1.5 md:mt-4 md:mb-6">
                  {geoLocationTitle.geoDescription &&
                  geoLocationTitle.isUSVisitor
                    ? geoLocationTitle.geoDescription
                    : title}
                </h1>
                <div
                  id="PremiumHero-description"
                  className="text-[17px] leading-9 md:leading-10 max-w-lg font-inter mb-0"
                >
                  {safelyRenderHtml(
                    `${description} ${
                      hasGeoCustomization ? geoDescription : ""
                    } `,
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full md:w-1/2 lg:w-1/3 hidden md:block">
            {image?.filename && (
              <Image
                src={readImage(image)}
                alt={image.alt || "hero image"}
                width={392}
                height={328}
                priority
                sizes="(max-width: 767px) 0px, 392px"
                className={classNames(
                  "w-[335px] h-[378px] md:w-[392px] md:h-[328px] order-first md:order-last mb-12 md:mb-0 object-contain object-center hidden md:block",
                )}
              />
            )}
          </div>
        </div>
        <div
          className={classNames(
            "relative lg:absolute lg:-bottom-24 max-w-7xl mx-auto md:flex justify-center md:bg-white rounded-1 shadow-sm px-6 md:px-5 lg:px-8 md:pt-10 md:pb-0 right-0 left-0",
            hasListingForm ? "md:h-[300px]" : "md:h-[180px]",
          )}
        >
          <div data-testid="form-atf-5" className="py-4">
            <PremiumHeroForm
              buttonLabel={buttonLabel}
              buttonLink={buttonLink}
              hasSwapCustomization={hasSwapCustomization}
              geoSessionValue={geoSessionValue as string}
              hideLabel
              font="font-inter"
              customClassName={PREMIUM_HERO_FORM_ATF_LAYOUT}
            />
          </div>
        </div>
      </section>

      <div className="px-6 mt-4 md:pt-2 hidden md:block">
        <ThumbnailSet
          blok={{
            items: testimonial,
            title: TestimonialTitle ?? "",
            subTitle: TestimonialSubtitle ?? "",
            compact: true,
          }}
        />
      </div>
      <div className="px-6 mt-4 md:hidden">
        <ThumbnailSet
          titleAsHeading={false}
          blok={{
            items: testimonial,
            title: TestimonialTitle ?? "",
            subTitle: TestimonialSubtitle ?? "",
            compact: true,
          }}
        />
      </div>
      <section className="md:mb-10" />
    </>
  )
}

export default PetsHeroATFTestimonial
